// Studio-wide context: the store, identity of the version being edited, view
// state shared by panels (zoom) and services (asset URLs, loaded fonts, the
// asset picker). Ephemeral: nothing here is persisted in the version.
import type { AssetRef, CreativeDocument, Trigger } from "@interactads/creative-model";
import { createContext, useContext, useEffect, useState } from "react";
import type { Edit } from "../core/editor";
import type { PlaybackClock } from "./playback";
import type { FocusStore } from "./component-focus";
import type { Asset } from "../api";
import type { StudioStore } from "../use-studio";

export type PickerPurpose =
  | { readonly kind: "add-image" }
  | { readonly kind: "replace-image"; readonly elementId: string }
  | { readonly kind: "fallback"; readonly layoutId: string; readonly width: number; readonly height: number }
  // 7.6E
  | { readonly kind: "add-video" }
  | { readonly kind: "replace-video"; readonly elementId: string }
  | { readonly kind: "poster"; readonly elementId: string }
  | { readonly kind: "add-audio" }
  | { readonly kind: "replace-audio"; readonly elementId: string }
  | { readonly kind: "slide-image"; readonly elementId: string }
  // 7.6F: a game placeholder (or both cards of a pair) becomes this image.
  | { readonly kind: "game-image"; readonly elementIds: readonly string[] };

/** Asset kind a picker purpose works with. */
export const pickerKind = (p: PickerPurpose): "image" | "video" | "audio" =>
  p.kind === "add-video" || p.kind === "replace-video"
    ? "video"
    : p.kind === "add-audio" || p.kind === "replace-audio"
      ? "audio"
      : "image";

export interface StudioContextValue {
  readonly orgId: string;
  readonly versionId: string;
  readonly brandId: string;
  readonly creativeId: string;
  readonly store: StudioStore;
  readonly readOnly: boolean;
  readonly edit: (edit: Edit) => void;
  readonly assetUrl: (ref: Pick<AssetRef, "assetId" | "revisionId">) => string;
  readonly loadedFonts: ReadonlySet<string>;
  readonly zoom: number;
  readonly setZoom: (zoom: number) => void;
  readonly fitToView: () => void;
  readonly openPicker: (purpose: PickerPurpose, onPicked: (asset: Asset) => void) => void;
  readonly openPreview: () => void;
  readonly openAddFormat: () => void;
  /** Playback clock of the current scene's timeline (outside React state). */
  readonly clock: PlaybackClock;
  /** Opens the interaction builder: a new rule (with defaults) or an existing one by id. */
  readonly openInteraction: (target: InteractionTarget) => void;
  /** Which carousel slide / survey question the canvas shows (ephemeral, 7.6E). */
  readonly focus: FocusStore;
  /** Opens the survey dialogs: create one (asks for its objective) or edit questions and branches. */
  readonly openSurvey: (mode: "new" | "edit") => void;
  /** Opens "Configurar juego" (7.6F). */
  readonly openGame: () => void;
}

export type InteractionTarget =
  | { readonly kind: "new"; readonly elementId?: string; readonly trigger?: Trigger }
  | { readonly kind: "edit"; readonly ruleId: string };

export const StudioContext = createContext<StudioContextValue | null>(null);

export function useStudio(): StudioContextValue {
  const value = useContext(StudioContext);
  if (!value) throw new Error("useStudio outside <StudioContext>");
  return value;
}

/**
 * Loads the document's brand fonts as FontFace objects (bytes fetched with the
 * session; namespaced family names so nothing collides with the app's fonts).
 */
export function useDocumentFonts(doc: CreativeDocument, assetUrl: StudioContextValue["assetUrl"]) {
  const [loaded, setLoaded] = useState<ReadonlySet<string>>(new Set());
  const signature = (doc.fonts ?? [])
    .map((f) => `${f.key}:${doc.assets.find((a) => a.key === f.asset)?.revisionId ?? ""}`)
    .join("|");
  useEffect(() => {
    let cancelled = false;
    const fonts = doc.fonts ?? [];
    void Promise.all(
      fonts.map(async (f) => {
        const asset = doc.assets.find((a) => a.key === f.asset);
        if (!asset || typeof FontFace === "undefined") return null;
        try {
          const response = await fetch(assetUrl(asset), { credentials: "include" });
          if (!response.ok) return null;
          const face = new FontFace(`ia-font-${f.key}`, await response.arrayBuffer());
          await face.load();
          document.fonts.add(face);
          return f.key;
        } catch {
          return null;
        }
      })
    ).then((keys) => {
      if (!cancelled) setLoaded(new Set(keys.filter((k): k is string => k !== null)));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when the pinned fonts change
  }, [signature]);
  return loaded;
}
